import _ from 'lodash'
import translations from '../../../utils/translations'
import { createFormContainerManifest } from './box-manifest'
import {
  FIELDS_ROLES,
  ROLE_DOWNLOAD_MESSAGE,
  ROLE_FORM,
  ROLE_MESSAGE,
  ROLE_SUBMIT_BUTTON,
  ROLE_TITLE,
  ROLE_PREVIOUS_BUTTON,
  ROLE_NEXT_BUTTON,
  REGISTRATION_FIELDS_ROLES,
  ROLE_SUBTITLE,
  ROLE_LIMIT_MESSAGE,
} from '../../../constants/roles'
import { createSubmitButtonManifest } from './submit-button-manifest'
import {
  createHiddenMessageManifest,
  createDownloadMessageManifest,
} from './hidden-message-manifest'
import { createTitleManifest } from './title-manifest'
import { AppStateBuilder, AppStateObject } from '../app-state/app-state-builder'
import { FormPlugin } from '@wix/forms-common'
import { createMultiStepManifest } from './multi-step-manifest'
import { createFirstTimeExperienceManifest } from './first-time-experience-manifest'
import { stepButtonManifest } from './step-button-manifest'
import { createFieldsManifests } from '../preset/fields/field-types-data'
import { createLimitMessageManifest } from './hidden-limit-message-manifest'
import { createComplexPhoneController } from './complex-phone-manifest'
import { pluginAppStatesConfigs } from '../plugins/pluginAppStates'
import { createComplexAddressController } from './complex-address-manifest'
import { fieldsStore } from '../preset/fields/fields-store'

export const OLD_CONTROLLER_TYPE = 'singlePostController'

const ALWAYS_HIDE_CONTROLLER = 'NEVER'

const pluginsInOldForms = [
  FormPlugin.FORM_BUILDER,
  FormPlugin.GET_SUBSCRIBERS,
  FormPlugin.REGISTRATION_FORM,
]

const withOldPluginState = (plugins: FormPlugin[]) =>
  plugins.length === 1 && _.includes(pluginsInOldForms, _.first(plugins))

const shouldAddNonDuplicateableState = (config: PluginAppStateConfig) =>
  config.isDuplicatable && withOldPluginState(config.plugins)

const duplicateableStates = (config: PluginAppStateConfig) => {
  const states = [config.isDuplicatable]
  const shouldAddFalse = shouldAddNonDuplicateableState(config)

  if (shouldAddFalse) {
    states.push(false)
  }

  return states
}

const statesByPluginsConfigs = (pluginConfigs: PluginAppStateConfig[]) =>
  _.flatMap(pluginConfigs, (pluginAppState) => {
    return _.flatMap(
      duplicateableStates(pluginAppState),
      (duplicatable) =>
        new AppStateBuilder({
          duplicatable,
          plugins: pluginAppState.plugins,
        }),
    )
  })

const statesByMainPlugin = (main: FormPlugin) => {
  const statesOfCotrollerType = _.filter(pluginAppStatesConfigs, { main })
  return statesByPluginsConfigs(statesOfCotrollerType)
}

const allStates = statesByPluginsConfigs(pluginAppStatesConfigs)
const oldFormsStates = _.filter(allStates, (state) => withOldPluginState(state.get().plugins))

export const getAppManifest = ({ isTopPremium, isResponsive, isKillingAscend }) => {
  const enhanceState = (state) => ({
    ...state,
    isTopPremium,
    isResponsive,
    isKillingAscend,
  })
  const subFieldRoles = _.values(fieldsStore.all())
    .filter((field) => field.subRole)
    .map((field) => field.subRole)

  const createFieldManifests = (controllerType: ControllerType | 'singlePostController') => {
    let filteredFieldsRoles = [...FIELDS_ROLES, ...subFieldRoles]

    if (!_.includes(['registrationForm', OLD_CONTROLLER_TYPE], controllerType)) {
      // filter registration fields for unrelated controller types
      filteredFieldsRoles = _.difference(filteredFieldsRoles, REGISTRATION_FIELDS_ROLES)
    }

    const fieldsManifest = createFieldsManifests()

    return filteredFieldsRoles.reduce(
      (res, fieldRole) => ({
        ...res,
        [fieldRole]: fieldsManifest[fieldRole],
      }),
      {},
    )
  }

  const titleManifest = createTitleManifest()

  const createManifest = (
    appState: AppStateObject,
    controllerType: ControllerType | 'singlePostController',
  ) => {
    const plugins = appState.plugins
    const state = enhanceState(appState)
    // form could be multiStep and also has another type
    const multiStepPluginExists = _.includes(plugins, FormPlugin.MULTI_STEP_FORM)
    const isMultiStepForm = controllerType === 'multiStepForm' || multiStepPluginExists

    const { box, steps } = isMultiStepForm
      ? createMultiStepManifest(state)
      : createFormContainerManifest(state)
    let pluginsMainRoles = {}

    if (isMultiStepForm) {
      pluginsMainRoles = {
        [ROLE_PREVIOUS_BUTTON]: stepButtonManifest(ROLE_PREVIOUS_BUTTON),
        [ROLE_NEXT_BUTTON]: stepButtonManifest(ROLE_NEXT_BUTTON),
      }
    }

    return {
      visibility: ALWAYS_HIDE_CONTROLLER,
      ...box,
      connections: {
        [ROLE_FORM]: box,
        [ROLE_SUBMIT_BUTTON]: createSubmitButtonManifest(state, controllerType),
        [ROLE_MESSAGE]: createHiddenMessageManifest(state, controllerType),
        [ROLE_DOWNLOAD_MESSAGE]: createDownloadMessageManifest(),
        [ROLE_LIMIT_MESSAGE]: createLimitMessageManifest(),
        [ROLE_TITLE]: titleManifest,
        [ROLE_SUBTITLE]: titleManifest,
        ...pluginsMainRoles,
        ...steps,
        ...createFieldManifests(controllerType),
      },
      ...createFirstTimeExperienceManifest(state, controllerType),
    }
  }

  const createDefaultStateWithStates = (
    states,
    controllerType: ControllerType | 'singlePostController',
  ) => {
    let defaultState = createManifest({}, controllerType)

    const controllerStates = {
      ...states.reduce((agg, state) => {
        const stateData = state.get()

        const stateManifest = createManifest(stateData, controllerType)

        if (!stateData.duplicatable && controllerType !== 'singlePostController') {
          defaultState = _.merge({}, defaultState, stateManifest)
        }

        agg[state.toString()] = stateManifest
        return agg
      }, {}),
    }

    const wannaBeDefault = _.find(controllerStates, (_v, k) => !_.includes(k, 'duplicatable'))

    return {
      default: _.merge({}, defaultState, wannaBeDefault),
      ...controllerStates,
    }
  }

  const controllersStageDataAppManifest: ControllerManifest = {
    controllersStageData: {
      singlePostController: createDefaultStateWithStates(oldFormsStates, OLD_CONTROLLER_TYPE), // TODO: [PERFORMANCE] UPDATE CONTROLLER ON INIT AND REMOVE THIS CONTROLLER TYPE
      wixForms: createDefaultStateWithStates(
        statesByMainPlugin(FormPlugin.FORM_BUILDER),
        'wixForms',
      ),
      getSubscribers: createDefaultStateWithStates(
        statesByMainPlugin(FormPlugin.GET_SUBSCRIBERS),
        'getSubscribers',
      ),
      registrationForm: createDefaultStateWithStates(
        statesByMainPlugin(FormPlugin.REGISTRATION_FORM),
        'registrationForm',
      ),
      multiStepForm: createDefaultStateWithStates(
        statesByMainPlugin(FormPlugin.MULTI_STEP_FORM),
        'multiStepForm',
      ),
      complexPhoneController: createComplexPhoneController(),
      complexAddressController: createComplexAddressController(),
    },
  }

  const manifestExports: ExportsManifest = {
    exports: {
      registrationForm: createCorvidAPIManifest(),
      wixForms: createCorvidAPIManifest(),
      getSubscribers: createCorvidAPIManifest(),
      multiStepForm: createCorvidAPIManifest(),
    },
  }

  const appDescriptor = {
    appDescriptor: {
      mainActions: [
        {
          title: translations.t('AppDefinition.MainAction.Title'),
          actionId: 'manageApp.mainCtaClicked',
          icon: 'add_plus_light_icon',
        },
      ],
      customActions: [
        {
          title: translations.t('AppDefinition.CustomAction.ManageSubmissions.Title'),
          actionId: 'manageApp.manageSubmissionsClicked',
          icon: 'appManager_settingsAction',
          type: 'dashboard',
        },
        {
          title: translations.t('AppDefinition.CustomAction.ViewTemplates.Title'),
          actionId: 'manageApp.viewTemplatesClicked',
          icon: 'appManager_addElementsAction',
          type: 'editorActions',
        },
      ],
      defaultActions: {
        ...(!isTopPremium
          ? {
              upgrade: {
                upgradeType: isKillingAscend ? 'SITE_UPGRADE' : 'APP_UPGRADE',
                upgradeText: translations.t(
                  isKillingAscend
                    ? 'AppDefinition.Upgrade.Premium.UpgradeText'
                    : 'AppDefinition.Upgrade.UpgradeText',
                ),
                upgradeLinkText: translations.t(
                  isKillingAscend
                    ? // TODO: add 'AppDefinition.Upgrade.BusinessPremium.UpgradeLinkText'
                      'AppDefinition.Upgrade.Premium.UpgradeLinkText'
                    : 'AppDefinition.Upgrade.UpgradeLinkText',
                ),
              },
            }
          : {}),
        learnMoreKB: '4aec8834-e911-4bf1-9b88-1ce05e03eb7f',
      },
    },
  }

  return {
    ...reduceAppManifest(controllersStageDataAppManifest),
    ...manifestExports,
    ...appDescriptor,
  }
}

const reduceAppManifest = (appManifest) => {
  const slimAppManifest = {
    controllersStageData: {},
  }

  _.forEach(appManifest.controllersStageData, (states, controllerType) => {
    const statesData = { default: {} }

    _.forEach(states, (stateData, state) => {
      if (state === 'default') {
        statesData.default = stateData
      } else {
        const diff = difference(stateData, appManifest.controllersStageData[controllerType].default)

        if (!_.isEmpty(diff)) {
          statesData[state] = diff
        }
      }
    })

    slimAppManifest.controllersStageData[controllerType] = { ...statesData }
  })
  return slimAppManifest
}

const difference = (object, base) => {
  return _.omitBy(
    _.transform(object, (result: any, value, key) => {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key]) ? difference(value, base[key]) : value
      }
    }),
    (value) => !_.isBoolean(value) && _.isEmpty(value),
  )
}

const createCorvidAPIManifest = () => ({
  members: {
    onWixFormSubmit: {
      kind: 'function',
      description:
        'Adds an event handler that fires when a visitor submits a Wix Form but before it is send to the server.',
      params: [
        {
          name: 'handler',
          description:
            'The name of the function or the function expression to run when a Wix Form submit occurred.',
        },
      ],
    },
  },
  eventHandlers: {
    onWixFormSubmitted: {
      type: 'wixFormSubmittedEvent',
      description:
        'Adds an event handler that fires when a visitor submits a Wix Form and it is successfully received by the server.',
      params: [
        {
          name: 'handler',
          description:
            'The name of the function or the function expression to run when a Wix Form is submitted.',
        },
      ],
    },
    onWixFormSubmittedError: {
      type: 'wixFormSubmittedErrorEvent',
      description:
        'Adds an event handler that fires when a visitor is not able to successfully submit a Wix Form to the server.',
      params: [
        {
          name: 'handler',
          description:
            'The name of the function or the function expression to run when a Wix Form is not submitted due to error.',
        },
      ],
    },
  },
  inherits: {},
})

// Maybe will be useful one day..
// const intersection = (object, base) => {
//   const changes = (object, base) => {
//     return _.omitBy(_.transform(object, (result: any, value, key) => {
// 			if (_.isEqual(value, base[key])) {
// 				result[key] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value;
// 			}
// 		}), value => !_.isBoolean(value) && _.isEmpty(value));
//   }

//   return changes(object, base)
// }
